import { useState, useEffect, useCallback } from 'react';
import { useApp, useAppState } from '../../../context/AppContext';
import RoleAPI from '../../../api/role';
import useErrorHandler from '../../../shared/hooks/useErrorHandler';
import { parseDataBySelect } from '../../../shared/utils/parseDataBySelect';
import { IconButton } from '../../../insfrastructure/ui/button';
import NavigationService from '../../../utils/history';

/**
 * Hook personalizado para manejar roles
 * Reemplaza RoleController
 * 
 * @returns {Object} Objeto con datos y funciones para manejar roles
 */
export const useRoles = () => {
    const { dispatch, notify } = useApp();
    const roles = useAppState('roles');
    const vrole = useAppState('vrole');
    const handleError = useErrorHandler();
    
    const [data, setData] = useState(roles || []);
    const [view, setView] = useState(vrole || null);
    const [isLoading, setIsLoading] = useState(false);
    
    // Estados para formulario
    const [rolPermissions, setRolePermissions] = useState([]);
    const [rolName, setRolName] = useState(null);
    const [edit, setEdit] = useState(false);
    const [confirmCreate, setConfirmCreate] = useState(false);
    const [openRemove, setOpenRemove] = useState(false);
    const [idRemove, setIdRemove] = useState(null);

    // Columnas para la tabla
    const columns = [
        { key: '_id', label: 'ID' },
        { key: 'name', label: "Nombre" },
        { key: 'actions', label: 'Acciones' }
    ];

    // Custom elements para la tabla
    const customElements = {
        actions: (element) => (
            <div>
                <IconButton onClick={() => goView(element._id)}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15.58 12C15.58 13.98 13.98 15.58 12 15.58C10.02 15.58 8.42004 13.98 8.42004 12C8.42004 10.02 10.02 8.42004 12 8.42004C13.98 8.42004 15.58 10.02 15.58 12Z" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M12 20.27C15.53 20.27 18.82 18.19 21.11 14.59C22.01 13.18 22.01 10.81 21.11 9.39997C18.82 5.79997 15.53 3.71997 12 3.71997C8.46997 3.71997 5.17997 5.79997 2.88997 9.39997C1.98997 10.81 1.98997 13.18 2.88997 14.59C5.17997 18.19 8.46997 20.27 12 20.27Z" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </IconButton>
                <IconButton onClick={() => openRemoveModal(element._id)}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 5.97998C17.67 5.64998 14.32 5.47998 10.98 5.47998C9 5.47998 7.02 5.57998 5.04 5.77998L3 5.97998" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M8.5 4.97L8.72 3.66C8.88 2.71 9 2 10.69 2H13.31C15 2 15.13 2.75 15.28 3.67L15.5 4.97" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M18.85 9.14001L18.2 19.21C18.09 20.78 18 22 15.21 22H8.79002C6.00002 22 5.91002 20.78 5.80002 19.21L5.15002 9.14001" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M10.33 16.5H13.66" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M9.5 12.5H14.5" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </IconButton>
            </div>
        ),
    };

    // Módulos de roles disponibles
    const modulesRole = [
        "usuarios", 
        "roles", 
        "academia-quiz",
        "academia-bootcamps",
        "talentos-trabajos",
        "talentos-perfiles",
        "talentos-aplicaciones",
        "talentos-empleabilidad", 
        "talentos-empleadores",
        "talentos-credit-codes",
        "ecosistema-usuarios",
        "cpt-campaings",
        "academia-liceos", 
        "talentos-jobs",
        "talentos-practices",
        "talentos-applications",
        "talentos-employers",
        "talentos-statistics",
        "talentos-credit-codes",
        "talentos-credit-codes-statistics"
    ];

    // Sincronizar con estado global
    useEffect(() => {
        if (roles) setData(roles);
    }, [roles]);

    useEffect(() => {
        if (vrole) {
            setView(vrole);
            setRolName(vrole?.name || '');
            setRolePermissions(vrole?.permissions || []);
        }
    }, [vrole]);

    /**
     * Obtener todos los roles
     */
    const getAll = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await RoleAPI.getAll();
            const rolesList = Array.isArray(res) 
                ? res.map(e => parseDataBySelect(e, e.name || 'Sin nombre'))
                : [];
            
            dispatch({ type: 'SET_ROLES', payload: rolesList });
            setData(rolesList);
            return rolesList;
        } catch (error) {
            handleError(error, 'Error al obtener los roles');
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, handleError]);

    /**
     * Obtener rol por ID
     */
    const getById = useCallback(async (id) => {
        if (!id) return null;

        setIsLoading(true);
        try {
            // Intentar obtener del estado local primero (optimización)
            const currentRoles = roles || [];
            const foundRole = currentRoles.find(e => e._id === id);
            
            if (foundRole) {
                dispatch({ type: 'SET_VROLE', payload: foundRole });
                setView(foundRole);
                setEdit(true);
            }

            // Siempre obtener del servidor para asegurar datos actualizados
            const res = await RoleAPI.getById(id);
            const roleData = res;
            
            dispatch({ type: 'SET_VROLE', payload: roleData });
            setView(roleData);
            setEdit(true);
            return roleData;
        } catch (error) {
            handleError(error, 'Error al obtener el rol');
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [roles, dispatch, handleError]);

    /**
     * Crear nuevo rol
     */
    const create = useCallback(async (data) => {
        setIsLoading(true);
        try {
            const res = await RoleAPI.create(data);
            await getAll(); // Refrescar lista
            notify({ 
                title: "Éxito!", 
                type: "success", 
                text: "Rol creado correctamente" 
            });
            return res;
        } catch (error) {
            handleError(error, 'Error al crear el rol');
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [getAll, notify, handleError]);

    /**
     * Actualizar rol
     */
    const update = useCallback(async (id, data) => {
        setIsLoading(true);
        try {
            const res = await RoleAPI.update(id, data);
            await getAll(); // Refrescar lista
            notify({ 
                title: "Éxito!", 
                type: "success", 
                text: "Rol actualizado correctamente" 
            });
            return res;
        } catch (error) {
            handleError(error, 'Error al actualizar el rol');
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [getAll, notify, handleError]);

    /**
     * Eliminar rol
     */
    const remove = useCallback(async (id) => {
        setIsLoading(true);
        try {
            const res = await RoleAPI.remove(id);
            await getAll(); // Refrescar lista
            notify({ 
                title: "Éxito!", 
                type: "success", 
                text: "Rol eliminado correctamente" 
            });
            return res;
        } catch (error) {
            handleError(error, 'Error al eliminar el rol');
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [getAll, notify, handleError]);

    /**
     * Guardar rol (create o update según edit)
     */
    const save = useCallback(async () => {
        if (!rolName) {
            notify({ 
                title: "Error", 
                type: "error", 
                text: "El nombre del rol es requerido" 
            });
            return;
        }

        const data = {
            name: rolName,
            permissions: rolPermissions
        };

        if (edit && view?._id) {
            await update(view._id, data);
        } else {
            await create(data);
        }
    }, [rolName, rolPermissions, edit, view, create, update, notify]);

    /**
     * Navegar a vista del rol
     */
    const goView = useCallback((id) => {
        NavigationService.navigateTo(`/config/roles/${id}`);
    }, []);

    /**
     * Navegar a crear nuevo rol
     */
    const goNew = useCallback(() => {
        NavigationService.navigateTo(`/config/roles/new`);
    }, []);

    /**
     * Navegar atrás
     */
    const goBack = useCallback(() => {
        NavigationService.navigateBack();
    }, []);

    /**
     * Abrir modal de eliminación
     */
    const openRemoveModal = useCallback((id) => {
        setIdRemove(id);
        setOpenRemove(true);
    }, []);

    /**
     * Eliminar rol (wrapper para mantener compatibilidad)
     */
    const removeRole = useCallback(async () => {
        if (!idRemove) {
            notify({ 
                title: "Error", 
                type: "error", 
                text: "No se ha seleccionado un rol para eliminar" 
            });
            return;
        }
        await remove(idRemove);
        setOpenRemove(false);
        setIdRemove(null);
    }, [idRemove, remove, notify]);

    return {
        // Datos
        data,
        roles,
        view,
        vrole,
        columns,
        customElements,
        modulesRole,
        
        // Estados del formulario
        rolPermissions,
        setRolePermissions,
        handlePermissionsChange: setRolePermissions,
        rolName,
        setRolName,
        edit,
        setEdit,
        confirmCreate,
        setConfirmCreate,
        openRemove,
        setOpenRemove,
        
        // Estado
        isLoading,
        
        // Funciones
        getAll,
        getById,
        create,
        update,
        remove: removeRole,
        save,
        handleSave: save, // Alias para compatibilidad
        goView,
        goNew,
        goBack,
        openRemoveModal,
        handleRemove: removeRole, // Alias para compatibilidad
        
        // Helpers
        refresh: getAll,
    };
};

export default useRoles;

